import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import SearchableDropdown from '../../../components/common/SearchableDropdown';

const WarehouseTransferForm = () => {
  const [formData, setFormData] = useState({
    from_warehouse_id: '',
    to_warehouse_id: '',
    transfer_date: '',
    commodity_id: '',
    quantity_mt: '',
    origin_order_id: '',
    remarks: '',
    from_cha_cma_id: '',
    to_cha_cma_id: ''
  });

  const [fromWarehouses, setFromWarehouses] = useState([]);
  const [toWarehouses, setToWarehouses] = useState([]);
  const [commodityOptions, setCommodityOptions] = useState([]);
  const [orderOptions, setOrderOptions] = useState([]);
  const [cmaOptions, setCmaOptions] = useState([]);
  const [chaOptions, setChaOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [fromSelectedType, setFromSelectedType] = useState('CMA'); // Default to CMA
  const [toSelectedType, setToSelectedType] = useState('CMA'); // Default to CMA
  const [availableQuantity, setAvailableQuantity] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const [commRes, orderRes, cmaRes, chaRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/masters/dropdown-commodities`, { credentials: 'include' }),
          fetch(`${API_BASE_URL}/api/masters/dropdown-import-order`, { credentials: 'include' }),
          fetch(`${API_BASE_URL}/api/masters/dropdown-cma-list`, { credentials: 'include' }),
          fetch(`${API_BASE_URL}/api/masters/dropdown-cha-list`, { credentials: 'include' })
        ]);

        const [commData, orderData, cmaData, chaData] = await Promise.all([
          commRes.json(), orderRes.json(), cmaRes.json(), chaRes.json()
        ]);

        if (commRes.ok) setCommodityOptions(commData.data || []);
        if (orderRes.ok) setOrderOptions(orderData.data || []);
        if (cmaRes.ok) setCmaOptions(cmaData.data || []);
        if (chaRes.ok) setChaOptions(chaData.data || []);
      } catch (error) {
        console.error('Dropdown load error:', error);
      }
    };

    fetchDropdowns();
  }, []);

  useEffect(() => {
    const fetchFromWarehouses = async () => {
      if (!formData.from_cha_cma_id) {
        setFromWarehouses([]);
        return;
      }
      const res = await fetch(`${API_BASE_URL}/api/masters/dropdown-warehouse-list-by-cma?cha_cma_id=${formData.from_cha_cma_id}`, { credentials: 'include' });
      const data = await res.json();
      if (res.ok) setFromWarehouses(data.data || []);
    };
    fetchFromWarehouses();
  }, [formData.from_cha_cma_id]);

  useEffect(() => {
    const fetchToWarehouses = async () => {
      if (!formData.to_cha_cma_id) {
        setToWarehouses([]);
        return;
      }
      const res = await fetch(`${API_BASE_URL}/api/masters/dropdown-warehouse-list-by-cma?cha_cma_id=${formData.to_cha_cma_id}`, { credentials: 'include' });
      const data = await res.json();
      if (res.ok) setToWarehouses(data.data || []);
    };
    fetchToWarehouses();
  }, [formData.to_cha_cma_id]);

  const getMaxDate = () => new Date().toISOString().split('T')[0]; // YYYY-MM-DD

  const handleChange = e => {
    const { name, value } = e.target;

    if (name === 'transfer_date' && value) {
      const maxDate = getMaxDate();
      if (value > maxDate) {
        setMessage({ type: 'error', text: 'Future dates are not allowed for Transfer Date.' });
        return;
      }
    }
    if (name === 'transfer_date') {
      setMessage(prev => (prev?.type === 'error' && prev?.text === 'Future dates are not allowed for Transfer Date.' ? null : prev));
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDropdownChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Auto-fill fields when Purchase Contract is selected
    if (name === 'origin_order_id' && value) {
      fetchWarehouseEntryData(value);
    }
    
    // Validate and auto-fill quantity when From Warehouse is selected
    if (name === 'from_warehouse_id' && value && formData.origin_order_id) {
      validateWarehouseQuantity(value, formData.origin_order_id);
    }
    
    // Clear quantity when From CHA/CMA changes
    if (name === 'from_cha_cma_id') {
      setFormData(prev => ({ ...prev, quantity_mt: '' }));
      setAvailableQuantity(0);
    }
  };

  const fetchWarehouseEntryData = async (orderId) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/masters/warehouse-entry-by-import-order?order_id=${orderId}`, { 
        credentials: 'include' 
      });
      const data = await res.json();
      
      if (res.ok && data.data) {
        const warehouseData = data.data;
        console.log('Received warehouse data:', warehouseData);
        
        // Don't auto-set the CMA/CHA type - let user choose manually
        
        // Wait a bit for CMA options to load, then auto-fill only commodity
        setTimeout(() => {
          // Set only commodity, let user manually select From fields and warehouse
          setFormData(prev => ({
            ...prev,
            commodity_id: parseInt(warehouseData.commodity_id) // Convert string to number
            // Removed auto-fill for quantity, from_cha_cma_id and from_warehouse_id
          }));
          
          console.log('Form data updated with:', {
            commodity_id: parseInt(warehouseData.commodity_id),
            check_cma_cha: warehouseData.check_cma_cha
          });
          
          console.log('Quantity details:', {
            received_qty_mt: warehouseData.received_qty_mt,
            issued_qty_mt: warehouseData.issued_qty_mt,
            transferred_qty_mt: warehouseData.transferred_qty_mt,
            available_qty_mt: warehouseData.available_qty_mt
          });
          
          // Show success message
          setMessage({ type: 'success', text: 'Commodity auto-filled! Please manually select From CHA/CMA, From Warehouse, and quantity will be validated.' });
        }, 500); // Wait 500ms for options to load
        
      } else {
        const errorMessage = data.message || data.error || 'No warehouse entry found for this contract';
        setMessage({ type: 'error', text: errorMessage });
      }
    } catch (error) {
      console.error('Error fetching warehouse entry data:', error);
      setMessage({ type: 'error', text: 'Error loading warehouse entry data' });
    }
  };

  const validateWarehouseQuantity = async (warehouseId, orderId) => {
    try {
      // Clear previous quantity and available quantity first
      setFormData(prev => ({ ...prev, quantity_mt: '' }));
      setAvailableQuantity(0);
      
      const apiUrl = `${API_BASE_URL}/api/masters/warehouse-entry-by-import-order?order_id=${orderId}&warehouse_id=${warehouseId}`;
      console.log('Validating warehouse quantity with URL:', apiUrl);
      
      const res = await fetch(apiUrl, { 
        credentials: 'include' 
      });
      const data = await res.json();
      
      console.log('API Response:', { status: res.status, data });
      
      if (res.ok && data.data) {
        const warehouseData = data.data;
        
        // Auto-fill quantity and set available quantity for validation
        setFormData(prev => ({
          ...prev,
          quantity_mt: warehouseData.available_qty_mt || 0
        }));
        
        setAvailableQuantity(parseFloat(warehouseData.available_qty_mt) || 0);
        
        const sanText = warehouseData.net_san_qty ? ` (Includes SAN Adj: ${warehouseData.net_san_qty > 0 ? '+' : ''}${warehouseData.net_san_qty} MT)` : '';
        
        setMessage({ 
          type: 'success', 
          text: `Quantity validated: ${warehouseData.available_qty_mt} MT available in selected warehouse${sanText}` 
        });
        
        console.log('Warehouse quantity validation for specific warehouse:', {
          warehouse_id: warehouseId,
          order_id: orderId,
          available_qty_mt: warehouseData.available_qty_mt,
          received_qty_mt: warehouseData.received_qty_mt,
          issued_qty_mt: warehouseData.issued_qty_mt,
          transferred_qty_mt: warehouseData.transferred_qty_mt
        });
      } else {
        const errorMessage = data.message || data.error || 'No stock available in selected warehouse for this contract';
        console.log('API Error Response:', data);
        setMessage({ 
          type: 'error', 
          text: errorMessage 
        });
        setAvailableQuantity(0);
        setFormData(prev => ({ ...prev, quantity_mt: '' }));
      }
    } catch (error) {
      console.error('Error validating warehouse quantity:', error);
      setMessage({ type: 'error', text: 'Error validating warehouse quantity' });
      setAvailableQuantity(0);
      setFormData(prev => ({ ...prev, quantity_mt: '' }));
    }
  };

  const handleFromTypeChange = (type) => {
    setFromSelectedType(type);
    // Clear From CMA/CHA and warehouse selections when type changes
    setFormData(prev => ({ 
      ...prev, 
      from_cha_cma_id: '',
      from_warehouse_id: '',
      quantity_mt: '' // Clear quantity when type changes
    }));
    setAvailableQuantity(0); // Reset available quantity
  };

  const handleToTypeChange = (type) => {
    setToSelectedType(type);
    // Clear To CMA/CHA and warehouse selections when type changes
    setFormData(prev => ({ 
      ...prev, 
      to_cha_cma_id: '',
      to_warehouse_id: ''
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();

    const maxDate = getMaxDate();
    if (formData.transfer_date && formData.transfer_date > maxDate) {
      setMessage({ type: 'error', text: 'Future dates are not allowed for Transfer Date.' });
      return;
    }

    setLoading(true);
    setMessage(null);

    // Validation: Check if transfer quantity exceeds available quantity
    const transferQuantity = parseFloat(formData.quantity_mt);
    if (transferQuantity > availableQuantity) {
      setMessage({ 
        type: 'error', 
        text: `Transfer quantity (${transferQuantity} MT) cannot exceed available quantity (${availableQuantity} MT)` 
      });
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/warehouse/warehouse-transfers-post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });

      const data = await res.json();
      if (res.ok) {
        setMessage({ type: 'success', text: 'Warehouse Transfer Created Successfully!' });
        setFormData({
          from_warehouse_id: '',
          to_warehouse_id: '',
          transfer_date: '',
          commodity_id: '',
          quantity_mt: '',
          origin_order_id: '',
          remarks: '',
          from_cha_cma_id: '',
          to_cha_cma_id: ''
        });
        setFromSelectedType('CMA'); // Reset to default CMA selection
        setToSelectedType('CMA'); // Reset to default CMA selection
      } else {
        setMessage({ type: 'error', text: data.error || 'Something went wrong' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Server Error' });
    }

    setLoading(false);
  };

  return (
    <div className="max-w-8xl mx-auto p-0 bg-white">
      <div className="flex justify-between items-center border-b border-gray-100 bg-gray-50 px-4 py-1.5">
        <h2 className="text-lg font-bold mb-0 text-left">Warehouse Transfer Entry</h2>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-[#10a5e9] border border-[#10a5e9] px-2 py-0.5 rounded text-xs font-medium hover:bg-[#eaf6fd]"
        >
          <FaArrowLeft />
          Back
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 px-4 py-4">
         
          <div>
            <label className="block text-xs font-medium mb-1">Purchase Contract No</label>
            <SearchableDropdown
              options={orderOptions.map(order => ({ id: order.order_id, name: `${order.purchase_contract_no} (${order.vessel_name})` }))}
              value={formData.origin_order_id}
              onChange={value => handleDropdownChange('origin_order_id', value)}
              placeholder="Select Purchase Contract"
              displayKey="name"
              valueKey="id"
            />
          </div>

          <div>
            <label className="block text-xs font-medium mb-1">Commodity</label>
            <SearchableDropdown
              options={commodityOptions}
              value={formData.commodity_id}
              onChange={value => handleDropdownChange('commodity_id', value)}
              placeholder="Select Commodity"
              displayKey="name"
              valueKey="commodity_id"
              required
              customDisplay={(item) => `${item.name} (${item.origin || 'N/A'})`}
            />
          </div>
 {/* CMA/CHA Checkbox Section for From */}
 <div className="md:col-span-2 flex gap-6 items-center mb-2">
            <label className="flex items-center gap-1">
              <input 
                type="checkbox" 
                checked={fromSelectedType === 'CMA'} 
                onChange={() => handleFromTypeChange('CMA')}
              /> 
              CMA
            </label>
            <label className="flex items-center gap-1">
              <input 
                type="checkbox" 
                checked={fromSelectedType === 'CHA'} 
                onChange={() => handleFromTypeChange('CHA')}
              /> 
              CHA
            </label>
          </div>

          <div>
            <label className="block text-xs font-medium mb-1">From {fromSelectedType}</label>
            <SearchableDropdown
              options={fromSelectedType === 'CMA' 
                ? cmaOptions
                    .filter(cma => cma.type === 'CMA' || cma.type === 'BOTH')
                    .map(cma => ({ id: cma.cha_cma_id, name: cma.name }))
                : chaOptions
                    .filter(cha => cha.type === 'CHA' || cha.type === 'BOTH')
                    .map(cha => ({ id: cha.cha_cma_id, name: cha.name }))
              }
              value={formData.from_cha_cma_id}
              onChange={value => handleDropdownChange('from_cha_cma_id', value)}
              placeholder={`Select From ${fromSelectedType}`}
              displayKey="name"
              valueKey="id"
              required
            />
          </div>

          <div>
            <label className="block text-xs font-medium mb-1">From Warehouse</label>
            <SearchableDropdown
              options={fromWarehouses.map(wh => ({ id: wh.warehouse_id, name: wh.name }))}
              value={formData.from_warehouse_id}
              onChange={value => handleDropdownChange('from_warehouse_id', value)}
              placeholder="Select From Warehouse"
              displayKey="name"
              valueKey="id"
              required
            />
          </div>

 {/* CMA/CHA Checkbox Section for To */}
 <div className="md:col-span-2 flex gap-6 items-center mb-2">
            <label className="flex items-center gap-1">
              <input 
                type="checkbox" 
                checked={toSelectedType === 'CMA'} 
                onChange={() => handleToTypeChange('CMA')}
              /> 
              CMA
            </label>
            <label className="flex items-center gap-1">
              <input 
                type="checkbox" 
                checked={toSelectedType === 'CHA'} 
                onChange={() => handleToTypeChange('CHA')}
              /> 
              CHA
            </label>
          </div>

          <div>
            <label className="block text-xs font-medium mb-1">To {toSelectedType}</label>
            <SearchableDropdown
              options={toSelectedType === 'CMA' 
                ? cmaOptions
                    .filter(cma => cma.type === 'CMA' || cma.type === 'BOTH')
                    .map(cma => ({ id: cma.cha_cma_id, name: cma.name }))
                : chaOptions
                    .filter(cha => cha.type === 'CHA' || cha.type === 'BOTH')
                    .map(cha => ({ id: cha.cha_cma_id, name: cha.name }))
              }
              value={formData.to_cha_cma_id}
              onChange={value => handleDropdownChange('to_cha_cma_id', value)}
              placeholder={`Select To ${toSelectedType}`}
              displayKey="name"
              valueKey="id"
              required
            />
          </div>

          <div>
            <label className="block text-xs font-medium mb-1">To Warehouse</label>
            <SearchableDropdown
              options={toWarehouses.map(wh => ({ id: wh.warehouse_id, name: wh.name }))}
              value={formData.to_warehouse_id}
              onChange={value => handleDropdownChange('to_warehouse_id', value)}
              placeholder="Select To Warehouse"
              displayKey="name"
              valueKey="id"
              required
            />
          </div>

          <div>
            <label className="block text-xs font-medium mb-1">Transfer Date</label>
            <input
              type="date"
              name="transfer_date"
              value={formData.transfer_date}
              onChange={handleChange}
              onKeyDown={(e) => { if (e.key !== 'Tab' && e.key !== 'Escape') e.preventDefault(); }}
              max={getMaxDate()}
              className="w-full border border-gray-300 px-3 py-1.5 text-sm cursor-pointer focus:outline-none focus:border-blue-400"
              title="Select date from calendar only (future dates not allowed)"
              required
            />
          </div>

          <div>
            <label className="block text-xs font-medium mb-1">Quantity (MT)</label>
            <input
              type="number"
              step="0.001"
              name="quantity_mt"
              value={formData.quantity_mt}
              onChange={handleChange}
              max={availableQuantity}
              className={`w-full border px-3 py-1.5 text-sm ${
                parseFloat(formData.quantity_mt) > availableQuantity 
                  ? 'border-red-400 focus:border-red-400' 
                  : 'border-gray-300 focus:border-blue-400'
              }`}
              required
            />
            {availableQuantity > 0 && (
              <div className="mt-1 text-xs text-gray-600">
                Available: {availableQuantity} MT
                {parseFloat(formData.quantity_mt) > availableQuantity && (
                  <span className="text-red-600 ml-2">⚠️ Exceeds available quantity</span>
                )}
              </div>
            )}
          </div>


          <div className="md:col-span-2">
            <label className="block text-xs font-medium mb-1">Remarks</label>
            <textarea
              name="remarks"
              value={formData.remarks}
              onChange={handleChange}
              className="w-full border border-gray-300 px-3 py-1.5 text-sm resize-none"
              rows={3}
            />
          </div>
        </div>

        <div className="px-4 pb-4">
          <button
            type="submit"
            className="w-auto px-4 py-1.5 text-sm bg-[#10a5e9] text-white font-semibold tracking-wide rounded border border-[#10a5e9] hover:bg-[#0e8ac2] transition"
            disabled={loading || parseFloat(formData.quantity_mt) > availableQuantity}
          >
            {loading ? 'Saving...' : 'Save Transfer'}
          </button>
          {message && (
            <div className={`mt-2 text-sm ${message.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
              {message.text}
            </div>
          )}
        </div>
      </form>
    </div>
  );
};

export default WarehouseTransferForm;